import { LitElement, css, html, unsafeCSS } from 'lit';
import styles from './toastGroup.scss?inline';
import { DEFAULT_POSITION, EVENTS, POSITION, STATUS, TOAST_ID_ATTR } from '../toast/constants';

const DEFAULT_OPTIONS = {
    durationShow: 200,
    durationHide: 200,
    durationVisible: 5000,
};

export default class SotbitToastGroup extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {};

    constructor() {
        super();
        this.options = { ...DEFAULT_OPTIONS };
    }

    /**
     * @param {string|number} id
     * @returns {boolean}
     */
    isActive(id) {
        return Boolean(this.getToastById(id));
    }

    /**
     * @param {string|number} id
     * @returns {HTMLElement|null}
     */
    getToastById(id) {
        if (!id) {
            return null;
        }

        const escapedId = CSS.escape(String(id));
        return this.renderRoot?.querySelector(`sotbit-toast[${TOAST_ID_ATTR}="${escapedId}"]`) || null;
    }

    /**
     * @param {object|string} options
     * @param {string} [options.message]
     * @param {string} [options.title]
     * @param {string} [options.status]
     * @param {'left'|'center'|'right'} [options.position]
     * @param {string|number} [options.id] — при повторном вызове с тем же id новый toast не создаётся
     * @param {{ text: string, href?: string, onClick?: Function }} [options.action]
     * @param {boolean} [options.closable]
     * @param {number|null} [options.duration] ms; 0/null/undefined — без auto-dismiss
     * @returns {HTMLElement|null}
     */
    addToast(options = {}) {
        const config = typeof options === 'string' ? { message: options } : options || {};

        if (!config.message && !config.title) {
            console.warn('<sotbit-toast-group>: message or title is required');
            return null;
        }

        const id = config.id;

        if (id) {
            const existing = this.getToastById(id);

            if (existing) {
                return existing;
            }
        }

        const element = document.createElement('sotbit-toast');
        const status = config.status || STATUS.ALERT;
        const closable = config.closable !== false;
        const duration = typeof config.duration === 'number' ? config.duration : this.options.durationVisible;
        const position = this._resolveMountPosition(config.position);

        element.setAttribute('status', status);
        element.setAttribute('position', position);

        if (id) {
            element.setAttribute(TOAST_ID_ATTR, String(id));
        }

        if (config.title) {
            element.heading = config.title;
        }

        if (config.message) {
            element.message = config.message;
        }

        element.closable = closable;

        if (config.action?.text) {
            element.actionText = config.action.text;

            if (config.action.href) {
                element.actionHref = config.action.href;
            }

            if (typeof config.action.onClick === 'function') {
                element.setActionHandler(config.action.onClick);
            }
        }

        let hideTimer = null;

        const clearHideTimer = () => {
            if (hideTimer !== null) {
                clearTimeout(hideTimer);
                hideTimer = null;
            }
        };

        element.addEventListener(
            EVENTS.CLOSE,
            () => {
                clearHideTimer();
            },
            { once: true }
        );

        const mount = () => {
            const column = this._getColumn(position);

            if (!column) {
                console.warn(`<sotbit-toast-group>: column "${position}" not found`);
                return;
            }

            column.appendChild(element);

            if (duration > 0) {
                hideTimer = setTimeout(() => {
                    element.close?.();
                }, duration);
            }
        };

        if (this.hasUpdated) {
            mount();
        } else {
            this.updateComplete.then(mount);
        }

        return element;
    }

    _getColumn(position) {
        return this.renderRoot?.querySelector(`[data-toast-position="${position}"]`) || null;
    }

    _isMobileViewport() {
        return window.matchMedia('(max-width: 1024px)').matches;
    }

    _resolveMountPosition(position) {
        if (this._isMobileViewport()) {
            return POSITION.CENTER;
        }

        return this._normalizeToastPosition(position);
    }

    _normalizeToastPosition(position) {
        const value = String(position || DEFAULT_POSITION).toLowerCase();

        if (value === POSITION.LEFT || value === POSITION.RIGHT || value === POSITION.CENTER) {
            return value;
        }

        return DEFAULT_POSITION;
    }

    render() {
        return html`
            <div class="toast-group__col toast-group__col--left" data-toast-position="${POSITION.LEFT}"></div>
            <div class="toast-group__col toast-group__col--center" data-toast-position="${POSITION.CENTER}"></div>
            <div class="toast-group__col toast-group__col--right" data-toast-position="${POSITION.RIGHT}"></div>
        `;
    }
}
